import { Collapse, Form, Radio } from 'antd';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { useEffect, useState } from 'react';
import { RevxMoney } from 'components/RevxMoney';
import { useTranslation } from 'react-i18next';
import { formatNumberToThousandths } from 'utils/number';
import { useDebounceFn } from 'ahooks';
import { DEBOUNCE_TIME, NOT_APPLICABLE } from 'utils/constants';
import {
  SkuSlaBuyerConfigDTO,
  SkuSlaBuyerRangeDTO,
  SkuSlaBuyerQtyRangeOptionEnum,
} from 'domains/slaDomain';
import {
  AutoSplitQuantity,
  rangeTypeEnum,
  autoSplitQuantity,
} from './slaSplitQuantity';
import slaService from '../../../../../services/slaService';
import { useAppDispatch } from '../../../../../app/hooks';

const { USE_MARKUP, PRICING_ENGINE, NO_ADDITIONAL } =
  SkuSlaBuyerQtyRangeOptionEnum;

const { updateSlaBuyer } = slaService;

enum markupEnum {
  markup = 'markup',
  markupValue = 'markupValue',
  priceEach = 'priceEach',
}

type SLABuyerPriceProps = {
  id: number;
  config: SkuSlaBuyerConfigDTO;
  maxQty: number;
};

const SLABuyerPrice = ({ id, config, maxQty }: SLABuyerPriceProps) => {
  const [showRanges, setShowRanges] = useState<boolean>(true);
  const { t } = useTranslation();
  const [buyerUnitPriceForm] = Form.useForm();
  const { sla } = useAppDispatch();

  useEffect(() => {
    const newConfig = (() => {
      const ranges = [...config.ranges];
      ranges.forEach((i, index) => {
        ranges[index] = {
          ...ranges[index],
          priceEach: i.priceEach || 0,
        };
      });
      return { ...config, ranges };
    })();
    setShowRanges(!!config.ranges.length);
    buyerUnitPriceForm.setFieldsValue({ ...newConfig, maxQty });
  }, [config, buyerUnitPriceForm, maxQty]);

  const { run: runUpdateBuyerPriceHandler } = useDebounceFn(
    async () => {
      await updateBuyerPrice();
    },
    { wait: DEBOUNCE_TIME + 200 }
  );

  const { run: runAutoSplitQuantity } = useDebounceFn(
    ({ index, level, value }: AutoSplitQuantity) => {
      autoSplitQuantity({ index, level, value }, buyerUnitPriceForm, false);
    },
    { wait: DEBOUNCE_TIME }
  );

  const updateBuyerPrice = async () => {
    const d = buyerUnitPriceForm.getFieldsValue();
    const res = await updateSlaBuyer(id, { ...config, ...d });
    sla.updateSlaSupplier(res);
  };

  const markupCalc = (n: number, k: markupEnum, rangeIndex: number) => {
    const ranges = buyerUnitPriceForm.getFieldsValue().ranges;
    const curData = ranges?.[rangeIndex] as SkuSlaBuyerRangeDTO;
    if (!curData) return <></>;
    let markup = curData.markup,
      markupValue = curData.markupValue,
      priceEach = curData.priceEach,
      pricingMarkup = 0;
    if (k === markupEnum.priceEach) {
      priceEach = n;
      markupValue = +((priceEach * markup) / 100).toFixed(6);
    } else if (k === markupEnum.markup) {
      markup = n;
      markupValue = +((markup / 100) * curData.priceEach).toFixed(6);
    } else {
      markupValue = n;
      markup = +((markupValue / priceEach) * 100).toFixed(6);
    }
    pricingMarkup = priceEach + markupValue || 0;
    ranges[rangeIndex] = {
      ...curData,
      ...{ priceEach, markup, markupValue, pricingMarkup },
    };
    buyerUnitPriceForm.setFieldsValue({ ranges: ranges });
  };

  const renderCollapseContent = (key: number) => {
    const genNameField = (name: string) => [key, name];
    const curData = (buyerUnitPriceForm.getFieldsValue()?.ranges?.[key] ||
      config.ranges[key]) as SkuSlaBuyerRangeDTO;
    if (!curData) return <></>;
    const option = curData?.option || NO_ADDITIONAL;
    const optionBlocks = {
      [NO_ADDITIONAL]: () => {
        return (
          <>
            <RevxFlexLayout className="pricing-block mr-30">
              <div className="label title">Pricing each</div>
              <div className="price bold">
                {curData.priceEachFromSupplierPrice !== null ? (
                  <RevxMoney
                    value={curData.priceEachFromSupplierPrice || 0}
                    decimalDigits={6}
                  />
                ) : (
                  NOT_APPLICABLE
                )}
              </div>
              <div className="bold">Supplier price</div>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-pricing mr-30">
              <Form.Item name={genNameField('priceEach')} label="Price each">
                <RevxInputNumber precision={6} prefix={'$'} min={0} />
              </Form.Item>
              <div className="bold">For storefront</div>
            </RevxFlexLayout>
          </>
        );
      },
      [USE_MARKUP]: () => {
        return (
          <>
            <RevxFlexLayout className="pricing-block mr-30">
              <div className="label title">Pricing price</div>
              <div className="price bold">
                {curData.priceEachFromSupplierPrice ? (
                  <RevxMoney
                    value={curData.priceEachFromSupplierPrice || 0}
                    decimalDigits={6}
                  />
                ) : (
                  NOT_APPLICABLE
                )}
              </div>
              <div className="bold">Supplier price</div>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-pricing mr-30">
              <Form.Item name={genNameField('priceEach')} label="Price each">
                <RevxInputNumber
                  min={0}
                  precision={6}
                  prefix={'$'}
                  onChange={(n) => {
                    markupCalc(n, markupEnum.priceEach, key);
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-markup">
              <Form.Item name={genNameField('markup')} label="Markup">
                <RevxInputNumber
                  min={0}
                  precision={2}
                  suffix={'%'}
                  onChange={(n) => {
                    markupCalc(n, markupEnum.markup, key);
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-markup-value ml-10 mr-30">
              <Form.Item
                name={genNameField('markupValue')}
                label=" "
                colon={false}
              >
                <RevxInputNumber
                  min={0}
                  precision={6}
                  prefix={'$'}
                  onChange={(n) => {
                    markupCalc(n, markupEnum.markupValue, key);
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="pricing-block">
              <div className="label title">Pricing price/markup</div>
              <div className="price bold">
                <RevxMoney
                  value={curData.markupValue + curData.priceEach || 0}
                  decimalDigits={6}
                />
              </div>
              <div className="bold">For storefront</div>
            </RevxFlexLayout>
          </>
        );
      },
      [PRICING_ENGINE]: () => {
        return (
          <>
            <RevxFlexLayout className="input-markup">
              <Form.Item
                name={genNameField('markup')}
                label="Markup"
                rules={[{ required: true }]}
              >
                <RevxInputNumber precision={2} suffix="%" min={0} />
              </Form.Item>
            </RevxFlexLayout>
          </>
        );
      },
    };
    return (
      <RevxFlexLayout className="panel-content" key={key}>
        <RevxFlexLayout className="panel-content__main" justify="justify-start">
          <RevxFlexLayout className="quantity-block mr-30">
            <Form.Item
              name={genNameField('low')}
              label="Lowest Quantity"
              className="mr-24"
            >
              <RevxInputNumber
                min={0}
                precision={0}
                onChange={(value) => {
                  runAutoSplitQuantity({
                    index: key,
                    level: rangeTypeEnum.low,
                    value,
                  });
                }}
              />
            </Form.Item>
            <Form.Item name={genNameField('high')} label="Highest Quantity">
              <RevxInputNumber
                min={0}
                precision={0}
                onChange={(value) => {
                  runAutoSplitQuantity({
                    index: key,
                    level: rangeTypeEnum.high,
                    value,
                  });
                }}
              />
            </Form.Item>
          </RevxFlexLayout>
          <RevxFlexLayout className="quantity-options mr-30">
            <Form.Item name={genNameField('option')}>
              <Radio.Group>
                <Radio value={NO_ADDITIONAL}>
                  {t(
                    'platform.sku.unitPrice.buyerQuantityModelRadio.noAdditional'
                  )}
                </Radio>
                <Radio value={USE_MARKUP}>
                  {t('platform.sku.unitPrice.buyerQuantityModelRadio.markup')}
                </Radio>
                <Radio value={PRICING_ENGINE}>
                  {t('platform.sku.unitPrice.buyerQuantityModelRadio.pricing')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </RevxFlexLayout>
          {optionBlocks[option]?.()}
          <RevxFlexLayout className="input-pricing ml-30">
            <Form.Item
              name={genNameField('turnaroundHours')}
              label="Turnaround Time"
            >
              <RevxInputNumber precision={2} min={0} suffix={'hours'} />
            </Form.Item>
            {option !== PRICING_ENGINE && <div style={{ height: 32 }} />}
          </RevxFlexLayout>
        </RevxFlexLayout>
      </RevxFlexLayout>
    );
  };

  return (
    <Collapse>
      <Collapse.Panel header="Manage buyer price turnaround time" key="1">
        <div className="unit-price buyer-page">
          <div className="unit-price__title">
            <h2>{t('platform.sku.unitPrice.buyerPrice')}</h2>
            {!showRanges ? (
              <div className="bold">
                {t('platform.sku.unitPrice.buyerPricingTips')}
              </div>
            ) : (
              <>
                <div className="bold">
                  {t('platform.sku.unitPrice.buyerUnitPriceTips')}
                </div>
                <div className="bold">
                  {t('platform.sku.unitPrice.buyerUnitPriceTips2')}
                </div>
              </>
            )}
          </div>
          <Form
            form={buyerUnitPriceForm}
            onValuesChange={async (e, allValues) => {
              await runUpdateBuyerPriceHandler();
            }}
            requiredMark={false}
          >
            <RevxFlexLayout className="mb-24">
              <RevxFlexLayout className="unit-price__quantity mr-100">
                <Form.Item name="maxQty" hidden={true}>
                  <input type="hidden" />
                </Form.Item>
                {t('platform.sku.unitPrice.maxQuantityLabel')}
                {formatNumberToThousandths(maxQty || 0)}
              </RevxFlexLayout>
              {/*<Form.Item
                name="sendToRfm"
                label={t('platform.sku.unitPrice.sendToRfm')}
              >
                <Radio.Group>
                  <Radio value={true}>{t('common.yes')}</Radio>
                  <Radio value={false}>{t('common.no')}</Radio>
                </Radio.Group>
              </Form.Item>*/}
            </RevxFlexLayout>
            <Form.List name="ranges">
              {(fields, { add, remove }, meta) => {
                return fields.map((f, index) => {
                  return <div key={index}>{renderCollapseContent(index)}</div>;
                });
              }}
            </Form.List>
          </Form>
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};

export default SLABuyerPrice;
